import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Box,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";

interface Option {
  value: string;
  label: string;
}

interface ProjectSelectProps {
  options: Option[];
  placeholder?: string;
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  width?: string;
  onCustomChange?: (option: Option) => void;
  variant?: "primary" | "secondary";
  disabled?: boolean;
  tooltipLabel?: string;
  onMenuClose?: () => void;
}

export const ProjectSelect: React.FC<ProjectSelectProps> = ({
  options = [],
  selectedValue,
  setSelectedValue,
  width,
  onCustomChange = () => null,
  placeholder, // keep placeholder optional
  variant = "primary",
  disabled = false,
  tooltipLabel,
  onMenuClose,
}) => {
  const { t } = useTranslation("common");
  const effectivePlaceholder = placeholder || t("projects.allProjects");
  const selectedOption = options?.find(
    (option) => option.value === selectedValue
  );
  const displayText = selectedOption
    ? selectedOption?.label
    : effectivePlaceholder;
  const variantStyles = {
    primary: {
      width: "400px",
      height: "44px",
      fontSize: "16px",
      px: "20px",
    },
    secondary: {
      width: "250px",
      height: "38px",
      fontSize: "14px",
      px: "15px",
    },
  } as const;
  const appliedVariant = variantStyles[variant] ?? variantStyles.primary;
  const appliedWidth = width || appliedVariant.width;

  const menuContent = (
    <Box position="relative" minW="0" flexShrink={1} width={appliedWidth}>
      <Menu
        placement="bottom-start"
        isLazy
        onClose={() => {
          // Call onMenuClose callback when menu closes to reset tooltip hover state
          onMenuClose?.();
        }}
      >
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          width={appliedWidth}
          height={appliedVariant.height}
          textAlign="left"
          fontWeight="400"
          fontSize={appliedVariant.fontSize}
          color={selectedOption ? "#374151" : "#9CA3AF"}
          bg="white"
          border="1px solid #D1D5DB"
          borderRadius="32px"
          px={appliedVariant.px}
          isDisabled={disabled}
          opacity={disabled ? 0.5 : 1}
          cursor={disabled ? "not-allowed" : "pointer"}
          _hover={
            disabled
              ? {}
              : {
                  borderColor: "#9CA3AF",
                  bg: "white",
                }
          }
          _active={
            disabled
              ? {}
              : {
                  borderColor: "#6366F1",
                  bg: "white",
                }
          }
          _focus={
            disabled
              ? {}
              : {
                  borderColor: "#6366F1",
                  boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                  bg: "white",
                }
          }
          sx={{
            justifyContent: "space-between",
            "& .chakra-button__icon": {
              marginLeft: "auto",
              marginRight: "0",
              color: "#374151",
              fontSize: "20px",
            },
          }}
        >
          <Text isTruncated>{displayText}</Text>
        </MenuButton>

        <MenuList
          // ⬇️ Forces MenuList to align exactly under MenuButton
          minW="unset !important"
          width={appliedWidth} // same width as MenuButton
          transform="translateY(0) !important" // fixes weird shifting
          bg="white"
          border="1px solid #E5E7EB"
          borderRadius="12px"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.05)"
          p="8px"
          maxH="300px"
          overflowY="auto"
          zIndex={10}
        >
          {options?.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                if (!disabled) {
                  setSelectedValue(option.value);
                  onCustomChange?.(option);
                }
              }}
              bg={selectedValue === option.value ? "#000000ff" : "transparent"}
              color={selectedValue === option.value ? "white" : "#000000ff"}
              borderRadius="8px"
              px="16px"
              py="12px"
              fontSize="16px"
              fontWeight="400"
              isDisabled={disabled}
              _hover={{
                bg: selectedValue === option.value ? "#000000ff" : "#F3F4F6",
              }}
              _focus={{
                bg: selectedValue === option.value ? "#000000ff" : "#F3F4F6",
              }}
              mb="4px"
              transition="all 0.15s ease"
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );

  if (disabled && tooltipLabel) {
    return <CommonTooltip label={tooltipLabel}>{menuContent}</CommonTooltip>;
  }

  return menuContent;
};
